import { PlusOutlined } from "@ant-design/icons"
import { BackButton } from "components/BackButton/BackButton"
import { Button } from "components/Button/Button"
import { Drawer } from "components/Drawer/Drawer"
import { ErrorStatus } from "components/ErrorStatus/ErrorStatus"
import { Form } from "components/Form/Form"
import { useNotification } from "components/Notification/Notification"
import { Radio } from "components/Radio/Radio"
import { useDisclosure } from "hooks/useDisclosure"
import { AdminVisitCreatePayload, MANUAL_VISIT_TYPES } from "interface/visit"
import { useCallback } from "react"
import { useAdminVisitCreate } from "services/visits"
import { SelectTeams } from "./SelectTeams"
import { SelectVisitPatient } from "./SelectVisitPatient"

export function RequestVisit() {
	const { notify } = useNotification()
	const disclose = useDisclosure()
	const [form] = Form.useForm<AdminVisitCreatePayload>()
	const create = useAdminVisitCreate()
	const label = "Request visit"
	const handleCancel = useCallback(() => {
		disclose.close()
		form.resetFields()
		create.reset()
	}, [create, disclose, form])
	const handleFinish = useCallback(
		(values: AdminVisitCreatePayload) => {
			create.mutate(values, {
				onSuccess: () => {
					notify.success({
						message: label,
						description: `${values.visitType} visit requested successfully`,
					})
					disclose.close()
					form.resetFields()
				},
			})
		},
		[create, disclose, form, notify],
	)

	return (
		<>
			<Button
				type="primary"
				size="large"
				icon={<PlusOutlined aria-hidden />}
				onClick={disclose.open}
			>
				{label}
			</Button>

			{disclose.isOpen && (
				<Drawer
					open
					aria-label={label}
					title={<BackButton onClick={handleCancel}>Visits</BackButton>}
					onClose={handleCancel}
				>
					<h2 className="font-headings text-3xl font-semibold">{label}</h2>
					<Form<AdminVisitCreatePayload>
						form={form}
						autoComplete="off"
						name="admin-visit-create"
						layout="vertical"
						aria-label="Visit request"
						initialValues={{ visitType: MANUAL_VISIT_TYPES[0] }}
						className="overflow-y-auto rounded-2xl bg-mint-0 p-2 xs:p-4"
						onFinish={handleFinish}
					>
						<Form.Item
							name="patientUserId"
							label="Patient"
							rules={[{ required: true, message: "Please pick a patient" }]}
						>
							<SelectVisitPatient />
						</Form.Item>
						<Form.Item
							name="visitType"
							label="Visit type"
							rules={[{ required: true, message: "Please pick a visit type" }]}
						>
							<Radio.Group options={VISIT_TYPE_OPTIONS} />
						</Form.Item>
						<Form.Item
							name="fieldSupportTeamId"
							label="Field support team"
							rules={[{ required: true, message: "Please pick a team" }]}
						>
							<SelectTeams
								aria-label="Field support team"
								placeholder="Select a team"
							/>
						</Form.Item>
						<Form.Item className="flex flex-col gap-2 pt-2">
							<Button
								type="primary"
								htmlType="button"
								loading={create.isPending}
								className="w-full"
								onClick={form.submit}
							>
								Confirm
							</Button>
							<ErrorStatus show={create.isError}>
								{create.error?.message ??
									"An unexpected error occurred. Please, try again."}
							</ErrorStatus>
						</Form.Item>
					</Form>
				</Drawer>
			)}
		</>
	)
}

const VISIT_TYPE_OPTIONS = MANUAL_VISIT_TYPES.map((type) => ({
	value: type,
	label: type,
}))
